import Img from 'src/components/shared/Img'
import PropTypes from 'prop-types'
import { resolveContentfulImageSrc } from 'src/common/resolveContentfulImageSrc'
import {
  trustMarkerEntryAlt,
  trustMarkerEntryClickPayload,
  trustMarkerEntryColor,
  trustMarkerEntryId,
  trustMarkerEntryImage,
  trustMarkerEntryLink,
  trustMarkerEntryLogo,
  trustMarkerEntryRating,
  trustMarkerEntryTotalReview,
} from 'src/common/trustMarkerEntryFields'
import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver'
import { useAnalytics } from 'src/common/analytics'
import ButtonLink from 'src/components/shared/ButtonLink'

import DynamicMarkers from './DynamicMarkers'

const TrustMarkers = ({
  trustMarkersCombination,
  isDynamic = false,
  isFullLink = true,
}) => {
  const { track } = useAnalytics()
  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (!isIntersecting) return
      track(
        {
          additional_info: {
            image: 'trust-marker-' + trustMarkersCombination?.id,
          },
          event_type: 'track',
        },
        {},
        'section_view'
      )
    },
    freezeOnceVisible: true,
  })

  const markers = (trustMarkersCombination?.trustMarker ?? []).filter((tm) =>
    isDynamic
      ? resolveContentfulImageSrc(
          trustMarkerEntryLogo(tm) || trustMarkerEntryImage(tm)
        )
      : resolveContentfulImageSrc(trustMarkerEntryImage(tm))
  )

  const handleClick = ({ trustMarker, i }) => {
    track(
      {
        event_type: 'track',
        click_id: trustMarker.id,
        click_position: i,
        click_type: 'Link Click',
        additional_info: {
          section: 'trust-marker-' + trustMarker.id,
          click_url: trustMarker.link,
        },
      },
      {},
      'button_click'
    )
  }

  return (
    <div
      ref={ref}
      className="flex flex-col items-center justify-center py-6 lg:flex-row"
    >
      {markers.map((trustMarker, i) => {
        const id = trustMarkerEntryId(trustMarker)
        const link = trustMarkerEntryLink(trustMarker)
        const clickPayload = trustMarkerEntryClickPayload(trustMarker)

        return (
          <div key={id} className="flex w-full flex-col lg:w-fit lg:flex-row">
            {i > 0 && (
              <div className="mx-12 hidden h-25 w-[1.5px] bg-gray-970 lg:block" />
            )}
            {i > 0 && (
              <div className="my-6 block h-[1.5px] w-full bg-gray-970 lg:hidden" />
            )}
            {isFullLink && (
              <ButtonLink
                href={link}
                className="relative flex items-center justify-center"
                disabled={!link}
                onClick={() => handleClick({ i, trustMarker: clickPayload })}
                target="blank"
              >
                {isDynamic && (
                  <DynamicMarkers
                    id={id}
                    alt={trustMarkerEntryAlt(trustMarker)}
                    image={
                      trustMarkerEntryLogo(trustMarker) ||
                      trustMarkerEntryImage(trustMarker)
                    }
                    rating={Number(trustMarkerEntryRating(trustMarker))}
                    totalReviews={Number(
                      trustMarkerEntryTotalReview(trustMarker)
                    )}
                    color={trustMarkerEntryColor(trustMarker)}
                  />
                )}
                {!isDynamic && (
                  <Img
                    src={resolveContentfulImageSrc(
                      trustMarkerEntryImage(trustMarker)
                    )}
                    alt={trustMarkerEntryAlt(trustMarker)}
                    width={300}
                    height={300}
                    className="h-20"
                    imgClassName="object-contain"
                    layout="intrinsic"
                  />
                )}
              </ButtonLink>
            )}
            {!isFullLink && (
              <div className="relative flex items-center justify-center">
                {isDynamic && (
                  <DynamicMarkers
                    id={id}
                    alt={trustMarkerEntryAlt(trustMarker)}
                    image={
                      trustMarkerEntryLogo(trustMarker) ||
                      trustMarkerEntryImage(trustMarker)
                    }
                    rating={Number(trustMarkerEntryRating(trustMarker))}
                    totalReviews={Number(
                      trustMarkerEntryTotalReview(trustMarker)
                    )}
                    color={trustMarkerEntryColor(trustMarker)}
                    link={link}
                    onClick={() =>
                      handleClick({ i, trustMarker: clickPayload })
                    }
                  />
                )}
                {!isDynamic && (
                  <Img
                    src={resolveContentfulImageSrc(
                      trustMarkerEntryImage(trustMarker)
                    )}
                    alt={trustMarkerEntryAlt(trustMarker)}
                    width={300}
                    height={300}
                    className="h-20"
                    imgClassName="object-contain"
                    layout="intrinsic"
                  />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

TrustMarkers.propTypes = {
  trustMarkersCombination: PropTypes.object,
  isDynamic: PropTypes.bool,
  isFullLink: PropTypes.bool,
}

export default TrustMarkers
export { default as TrustMarkersWithLogosGrid } from './TrustMarkersWithLogosGrid'
